import Button from "@/components/ui/button";
import { handleAsyncOperation } from "@/shared";
import type { TSavedSlide } from "@/shared/types";
import { useDeleteSlideMutation } from "@api/slides-library";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";

const DIALOG_ID = "permanent-delete-slide-dialog";

const SlidePermanentDeleteDialog = ({ slide }: { slide: TSavedSlide }) => {
  const dispatch = useAppDispatch();
  const [deleteSlide, { isLoading }] = useDeleteSlideMutation();

  const handleClose = useCallback(
    () => dispatch(closeDialog(DIALOG_ID)),
    [dispatch],
  );

  const handleSubmit = useCallback(() => {
    if (isLoading) return;
    handleAsyncOperation({
      operation: async () => {
        await deleteSlide({ id: slide._id }).unwrap();
        toast.success("Slide deleted permanently");
        handleClose();
      },
      onError: () => toast.error("Failed to delete slide"),
    });
  }, [deleteSlide, handleClose, isLoading, slide._id]);

  return (
    <DialogBase>
      <DialogBase.Header title="Delete permanently" onClose={handleClose} />
      <DialogBase.Content className="tw-pt-4">
        <p className="tw-m-0 tw-text-[13px] tw-font-normal tw-text-blue">
          Are you sure that you want to permanently delete{" "}
          <span className="tw-font-semibold">“{slide.title}”</span> slide?
          <br />
          <br />
          This action cannot be undone.
        </p>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={isLoading}
          className={cn(isLoading && "tw-cursor-not-allowed")}
          variant="destructive"
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete permanently"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

SlidePermanentDeleteDialog.id = DIALOG_ID;
export { SlidePermanentDeleteDialog };
